import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Check, Gift, TrendingUp, Users, Image, Loader2 } from 'lucide-react';
import { ambassadorApply } from '../api';
import { useToast } from '../hooks/use-toast';

const AGREEMENT = [
  'I will represent Prestige Elements honestly and in line with the brand\u2019s holistic, feminine and luxury values.',
  'I understand commissions are earned only on completed, paid orders attributed to my unique referral code or link.',
  'I will not make medical, financial or unverified claims about products or services.',
  'I will not use spam, misleading advertising, or trademark-infringing content.',
  'Prestige Elements may adjust commission rates, campaigns and program terms with notice, and may deactivate accounts that violate these terms.',
  'Brand assets are provided for approved promotional use only and remain the property of Prestige Elements.',
];

const BecomeAmbassador = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '', social: '', audience: '', why: '', shipping_address: '', accepted_terms: false });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.accepted_terms) { toast({ title: 'Please accept the agreement', description: 'You must accept the Brand Ambassador Agreement to apply.' }); return; }
    setSubmitting(true);
    try {
      await ambassadorApply(form);
      setDone(true);
    } catch (err) {
      const msg = err?.response?.status === 409 ? 'An application already exists for this email.' : 'Could not submit application. Please try again.';
      toast({ title: 'Application error', description: msg });
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="pt-40 pb-40 min-h-screen">
        <div className="max-w-lg mx-auto px-5 text-center card-glass rounded-3xl p-12">
          <div className="h-16 w-16 mx-auto rounded-full bg-gradient-to-br from-[#d6197d] to-[#ff4da6] flex items-center justify-center mb-6"><Check className="h-8 w-8 text-white" /></div>
          <h1 className="font-cinzel text-3xl text-gold-gradient">Application Received</h1>
          <p className="font-cormorant text-xl text-[#f5ecd9]/75 mt-4">Thank you, {form.name}! Our team will review your application. Once approved, you can log in to your Ambassador dashboard.</p>
          <Link to="/ambassador/login" className="btn-gold inline-flex items-center gap-2 mt-8 rounded-full px-8 py-3.5 text-sm font-semibold text-[#0a0708] bg-gradient-to-r from-[#d4af37] via-[#f0d582] to-[#d4af37]">Go to Ambassador Login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-28 pb-24 min-h-screen">
      <section className="relative py-12 overflow-hidden">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 h-[420px] w-[420px] rounded-full bg-[#d6197d]/15 blur-[120px] glow-pulse" />
        <div className="relative max-w-3xl mx-auto px-5 text-center">
          <Sparkles className="h-12 w-12 text-[#ff4da6] mx-auto mb-5 floaty" />
          <p className="font-jost text-xs tracking-[0.4em] text-[#ff4da6] uppercase mb-3">Join The Circle</p>
          <h1 className="font-cinzel text-4xl md:text-6xl text-gold-gradient">Become a Brand Ambassador</h1>
          <div className="gold-divider my-6" />
          <p className="font-cormorant text-xl md:text-2xl text-[#f5ecd9]/75">Share the beauty of Prestige Elements, earn commission on every completed, qualifying sale made through your ambassador code, and unlock exclusive perks and campaigns.</p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 py-8">
        <div className="grid md:grid-cols-4 gap-6 mb-12">
          {[{ icon: TrendingUp, t: 'Earn Commission', d: 'On every completed, paid order via your code' }, { icon: Gift, t: 'Ambassador Kit', d: 'Free or discounted products' }, { icon: Image, t: 'Brand Assets', d: 'Photos, logos & captions' }, { icon: Users, t: 'Exclusive Campaigns', d: 'Ambassador-only drops' }].map((p) => (
            <div key={p.t} className="card-glass rounded-2xl p-6 text-center"><div className="h-12 w-12 mx-auto rounded-full border border-[#d4af37]/30 flex items-center justify-center mb-3"><p.icon className="h-5 w-5 text-[#f0d582]" /></div><h3 className="font-cinzel text-sm text-[#f5ecd9]">{p.t}</h3><p className="font-jost text-xs text-[#f5ecd9]/50 mt-1">{p.d}</p></div>
          ))}
        </div>

        <form onSubmit={submit} className="grid md:grid-cols-2 gap-10">
          <div className="card-glass rounded-2xl p-8 space-y-4">
            <h2 className="font-cinzel text-xl text-[#f0d582] mb-2">Your Application</h2>
            {[['name', 'Full Name', 'text'], ['email', 'Email', 'email'], ['password', 'Create a Password', 'password'], ['phone', 'Phone', 'tel'], ['social', 'Social Handle / Link', 'text'], ['audience', 'Audience Size / Platforms', 'text'], ['shipping_address', 'Shipping Address', 'text']].map(([k, l, t]) => (
              <div key={k}><label className="font-jost text-xs uppercase tracking-widest text-[#f5ecd9]/60">{l}</label><input type={t} required={['name', 'email', 'password'].includes(k)} value={form[k]} onChange={(e) => set(k, e.target.value)} className="mt-2 w-full bg-white/5 border border-[#d4af37]/30 rounded-md px-4 py-3 text-sm text-[#f5ecd9] focus:outline-none focus:border-[#ff4da6] transition-colors" /></div>
            ))}
            <div><label className="font-jost text-xs uppercase tracking-widest text-[#f5ecd9]/60">Why do you want to join?</label><textarea rows={3} value={form.why} onChange={(e) => set('why', e.target.value)} className="mt-2 w-full bg-white/5 border border-[#d4af37]/30 rounded-md px-4 py-3 text-sm text-[#f5ecd9] focus:outline-none focus:border-[#ff4da6] resize-none" /></div>
          </div>

          <div className="space-y-6">
            <div className="card-glass rounded-2xl p-8">
              <h2 className="font-cinzel text-xl text-[#f0d582] mb-4">Brand Ambassador Agreement</h2>
              <ul className="space-y-3">
                {AGREEMENT.map((a, i) => (<li key={i} className="flex items-start gap-3 font-jost text-sm text-[#f5ecd9]/70"><Check className="h-4 w-4 text-[#ff4da6] flex-shrink-0 mt-0.5" /> {a}</li>))}
              </ul>
              <p className="font-jost text-[10px] text-[#f5ecd9]/40 mt-4">Draft agreement — final legal terms to be confirmed by owner.</p>
              <label className="flex items-center gap-3 mt-5 font-jost text-sm text-[#f5ecd9]/80 cursor-pointer"><input type="checkbox" checked={form.accepted_terms} onChange={(e) => set('accepted_terms', e.target.checked)} /> I have read and accept the Brand Ambassador Agreement.</label>
            </div>
            <button type="submit" disabled={submitting} className="btn-gold w-full flex items-center justify-center gap-2 rounded-full py-4 text-sm font-semibold text-[#0a0708] bg-gradient-to-r from-[#d4af37] via-[#f0d582] to-[#d4af37] disabled:opacity-60">{submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</> : 'Submit Application'}</button>
            <p className="text-center font-jost text-sm text-[#f5ecd9]/50">Already approved? <Link to="/ambassador/login" className="text-[#f0d582] hover:text-[#ff4da6] underline">Log in here</Link></p>
          </div>
        </form>
      </section>
    </div>
  );
};

export default BecomeAmbassador;
